import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { DerivedFigure, StatusChip, VarianceBadge } from '../../components/qaqc/DerivedFigure.js';
import { ApiError, createPeriod, fetchApprovals, fetchPeriods } from '../../lib/api.js';
import { ProjectPicker, useProjects } from '../../components/qaqc/ProjectPicker.js';
import { t, formatDate } from '../../lib/i18n.js';

/** First day of the current month — the period identity the API expects. */
function currentPeriodStart(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * The monthly report list, and the project + period selector that opens one.
 *
 * Only the periods the user's scope reaches are listed — that narrowing happens
 * server-side and is the *scope*, not a permission error (ADR-0005).
 */
export function PeriodList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useState('');
  const [periodStart, setPeriodStart] = useState(currentPeriodStart());
  const [error, setError] = useState<string | null>(null);
  const { label } = useProjects();

  const { data, isLoading } = useQuery({ queryKey: ['qaqc-periods'], queryFn: fetchPeriods });

  const open = useMutation({
    mutationFn: () => createPeriod(projectId, periodStart),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['qaqc-periods'] });
      navigate(`/qaqc/periods/${result.period.id}`);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : t("Could not open that period.")),
  });

  return (
    <div className="min-h-full">
      <AppTopBar app="qaqc" />
      <Breadcrumbs app="qaqc" trail={['Monthly reports']} />

      <main className="mx-auto max-w-7xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Monthly QA/QC Reports")}</h1>

        <section className="mt-6 rounded-md border border-border bg-surface p-5 shadow-card">
          <h2 className="m-0 text-base font-semibold">{t("Open a reporting period")}</h2>
          <p className="mt-1 text-sm text-content-secondary">
            {t("Opening an existing period returns it rather than creating a second one.")}
          </p>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="min-w-72 flex-1">
              <label htmlFor="project-id" className="text-sm font-medium">
                {t("Project")}
              </label>
              <ProjectPicker
                id="project-id"
                value={projectId}
                onChange={setProjectId}
                className="mt-1.5 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="period-start" className="text-sm font-medium">
                {t("Period")}
              </label>
              <input
                id="period-start"
                type="month"
                value={periodStart.slice(0, 7)}
                onChange={(event) => setPeriodStart(`${event.target.value}-01`)}
                className="mt-1.5 rounded-sm border border-border-strong px-3 py-2 text-sm"
              />
            </div>
            <button
              type="button"
              disabled={!projectId || open.isPending}
              onClick={() => {
                setError(null);
                open.mutate();
              }}
              className="rounded-sm bg-ag-purple px-4 py-2 text-sm font-medium text-content-inverse disabled:opacity-60"
            >
              {t("Open")}
            </button>
          </div>

          {error && (
            <p role="alert" className="mt-3 rounded-sm bg-tone-critical-surface px-3 py-2 text-sm text-tone-critical">
              {error}
            </p>
          )}
        </section>

        <section className="mt-6">
          <h2 className="m-0 text-base font-semibold">{t("Recent periods")}</h2>
          {isLoading && <p className="text-content-secondary">{t("Loading…")}</p>}

          {data && data.periods.length === 0 && (
            <p className="mt-3 rounded-md border border-border bg-tone-info-surface p-4 text-sm text-content-secondary">
              {t("No reporting periods yet for the projects you can see.")}
            </p>
          )}

          <ul className="mt-3 grid list-none grid-cols-1 gap-4 p-0 lg:grid-cols-2">
            {data?.periods.map((period) => (
              <li key={period.id} className="rounded-md border border-border bg-surface p-5 shadow-card">
                <div className="flex items-start justify-between gap-3">
                  <Link
                    to={`/qaqc/periods/${period.id}`}
                    className="text-base font-semibold text-ag-purple no-underline hover:underline"
                  >
                    {period.periodStart.slice(0, 7)}
                  </Link>
                  <StatusChip status={period.status} />
                </div>
                <p className="m-0 mt-1 text-sm text-content-secondary">{label(period.projectId)}</p>
                <dl className="mt-4 flex flex-wrap items-end gap-8">
                  <DerivedFigure
                    label={t("Quality index")}
                    value={period.pqiValue}
                    explanation={period.pqiExplanation}
                  />
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-content-secondary">
                      {t("Month over month")}
                    </dt>
                    <dd className="m-0 mt-1">
                      <VarianceBadge value={period.pqiVariance} />
                    </dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}

/** The approver's queue (spec §9 key screens). */
export function ApprovalInbox() {
  const { data, isLoading } = useQuery({ queryKey: ['qaqc-approvals'], queryFn: fetchApprovals });
  const { label: projectLabel } = useProjects();

  return (
    <div className="min-h-full">
      <AppTopBar app="qaqc" />
      <Breadcrumbs app="qaqc" trail={['Approval inbox']} />

      <main className="mx-auto max-w-7xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Approval inbox")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Reports waiting on approval. You cannot approve one you submitted yourself.")}
        </p>

        {isLoading && <p className="mt-4 text-content-secondary">{t("Loading…")}</p>}

        {data && data.pending.length === 0 && (
          <p className="mt-4 rounded-md border border-border bg-tone-info-surface p-4 text-sm text-content-secondary">
            {t("Nothing is waiting on you.")}
          </p>
        )}

        <ul className="mt-4 grid list-none grid-cols-1 gap-4 p-0 lg:grid-cols-2">
          {data?.pending.map((period) => (
            <li key={period.id} className="rounded-md border border-border bg-surface p-5 shadow-card">
              <div className="flex items-start justify-between gap-3">
                <Link
                  to={`/qaqc/periods/${period.id}`}
                  className="text-base font-semibold text-ag-purple no-underline hover:underline"
                >
                  {period.periodStart.slice(0, 7)}
                </Link>
                <StatusChip status={period.status} />
              </div>
              <p className="m-0 mt-1 text-sm text-content-secondary">{projectLabel(period.projectId)}</p>
              <dl className="mt-4">
                <DerivedFigure
                  label={t("Quality index")}
                  value={period.pqiValue}
                  explanation={period.pqiExplanation}
                />
              </dl>
              {period.submittedAt && (
                <p className="mt-3 text-xs text-content-secondary">
                  {t("Submitted")}{' '}{formatDate(period.submittedAt)}
                </p>
              )}
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
